import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, Loader2, Store, Truck, Banknote, Smartphone, Wallet, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { API, fmt, formatApiErrorDetail, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import axios from "axios";

const DELIVERY_FEE = 2.0;
const FREE_DELIVERY_AT = 40;

const PAYMENT_METHODS = [
  { id: "applepay", label: "Apple Pay", desc: "Number drops down", icon: Smartphone },
  { id: "cashapp", label: "CashApp", desc: "Link drops down", icon: Banknote },
  { id: "venmo", label: "Venmo", desc: "Link drops down", icon: Wallet },
];

export const CartDrawer = () => {
  const { items, updateQty, removeFromCart, clearCart, subtotal, isOpen, setIsOpen } = useCart();
  const navigate = useNavigate();
  const [fulfillment, setFulfillment] = useState("pickup");
  const [paymentMethod, setPaymentMethod] = useState("applepay");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [placing, setPlacing] = useState(false);
  const [paySettings, setPaySettings] = useState({});
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then((r) => setPaySettings(r.data.payment || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isOpen) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const deliveryFee = fulfillment === "delivery" && subtotal < FREE_DELIVERY_AT ? DELIVERY_FEE : 0;
  const freeDelivery = appliedPromo?.type === "free_delivery";
  const effectiveFee = freeDelivery ? 0 : deliveryFee;
  let itemDiscount = 0;
  if (appliedPromo?.type === "percent") {
    itemDiscount = (subtotal * appliedPromo.value) / 100;
  } else if (appliedPromo?.type === "bxgy" && appliedPromo.buy_x >= 1 && appliedPromo.get_y >= 1) {
    const prices = items.flatMap((i) => Array(i.quantity).fill(i.price)).sort((a, b) => a - b);
    const chunk = appliedPromo.buy_x + appliedPromo.get_y;
    for (let i = 0; i + chunk <= prices.length; i += chunk) {
      itemDiscount += prices.slice(i, i + appliedPromo.get_y).reduce((a, b) => a + b, 0);
    }
  }
  itemDiscount = Math.round(itemDiscount * 100) / 100;
  const total = subtotal - itemDiscount + effectiveFee;
  const progress = Math.min(100, (subtotal / FREE_DELIVERY_AT) * 100);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoBusy(true);
    try {
      const { data } = await axios.get(`${API}/promo/validate`, { params: { code: promoInput.trim() } });
      setAppliedPromo(data);
      setPromoInput("");
      toast.success(`Promo ${data.code} applied!`);
    } catch {
      toast.error("That promo code isn't valid");
    } finally {
      setPromoBusy(false);
    }
  };

  const cashtag = paySettings.cashtag || "$PickleMeOK405";
  const venmoUser = (paySettings.venmo || "PickleMeOK405").replace(/^@/, "");
  const applePay = paySettings.apple_pay || "405-662-9638";

  const placeOrder = async () => {
    if (!name.trim()) {
      toast.error("Please add your name so we know whose jar is whose.");
      return;
    }
    if (fulfillment === "delivery" && !address.trim()) {
      toast.error("Please add a delivery address.");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        fulfillment,
        payment_method: paymentMethod,
        customer_name: name,
        customer_phone: phone,
        customer_email: email,
        delivery_address: address,
        promo_code: appliedPromo?.code || "",
      });
      clearCart();
      setIsOpen(false);
      navigate(`/order/confirmed/${data.order_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPlacing(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-[70]"
            data-testid="cart-drawer-overlay"
            data-lenis-prevent
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            className="fixed right-0 top-0 h-full w-full sm:w-[440px] bg-parchment z-[80] flex flex-col shadow-2xl"
            data-testid="cart-drawer"
            data-lenis-prevent
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-parchborder">
              <h2 className="font-serif font-bold text-xl text-dill">Your Basket</h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-full hover:bg-parchdeep transition-colors"
                aria-label="Close cart"
                data-testid="cart-drawer-close-button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {items.length === 0 ? (
                <div className="text-center py-16" data-testid="cart-empty-state">
                  <p className="font-serif text-2xl text-dill/60 italic">The shelf is bare.</p>
                  <p className="text-sm text-ink/50 mt-2">Add a jar or two — your jaw will thank you.</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {items.map((item) => (
                    <motion.div
                      key={item.id}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex gap-4 bg-white rounded-xl border border-parchborder p-3"
                      data-testid={`cart-item-${item.id}`}
                    >
                      <img
                        src={imgUrl(item.image)}
                        alt={item.name}
                        className="w-16 h-16 rounded-lg object-cover shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-serif font-semibold text-dill text-sm truncate">{item.name}</p>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-ink/45">{item.size}</p>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center border border-parchborder rounded-full">
                            <button
                              onClick={() => updateQty(item.id, item.quantity - 1)}
                              className="p-1.5 hover:text-terra transition-colors"
                              aria-label="Decrease"
                              data-testid={`cart-qty-dec-${item.id}`}
                            >
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="w-7 text-center font-mono text-xs font-semibold">{item.quantity}</span>
                            <button
                              onClick={() => updateQty(item.id, Math.min(item.stock ?? 50, item.quantity + 1))}
                              disabled={item.stock != null && item.quantity >= item.stock}
                              className="p-1.5 hover:text-terra transition-colors disabled:opacity-30 disabled:hover:text-current"
                              aria-label="Increase"
                              data-testid={`cart-qty-inc-${item.id}`}
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <span className="font-serif font-bold text-ink">{fmt(item.price * item.quantity)}</span>
                        </div>
                        {item.stock != null && item.quantity >= item.stock && (
                          <p className="text-[10px] text-terra font-semibold mt-1" data-testid={`cart-stock-hint-${item.id}`}>
                            That's the whole batch — only {item.stock} left.
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="self-start p-1 text-ink/30 hover:text-terra transition-colors"
                        aria-label="Remove item"
                        data-testid={`cart-remove-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}

              {items.length > 0 && (
                <div className="mt-8 space-y-5">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50 mb-3">
                      How are you getting your pickles?
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setFulfillment("pickup")}
                        className={`rounded-xl border-2 p-4 text-left transition-colors duration-300 ${
                          fulfillment === "pickup" ? "border-dill bg-dill/5" : "border-parchborder hover:border-dill/40"
                        }`}
                        data-testid="fulfillment-pickup-toggle"
                      >
                        <Store className={`w-5 h-5 mb-2 ${fulfillment === "pickup" ? "text-dill" : "text-ink/40"}`} />
                        <p className="font-semibold text-sm text-ink">Local Pickup</p>
                        <p className="text-xs text-ink/55 mt-0.5">Free — Wellston, OK</p>
                      </button>
                      <button
                        onClick={() => setFulfillment("delivery")}
                        className={`rounded-xl border-2 p-4 text-left transition-colors duration-300 ${
                          fulfillment === "delivery" ? "border-dill bg-dill/5" : "border-parchborder hover:border-dill/40"
                        }`}
                        data-testid="fulfillment-delivery-toggle"
                      >
                        <Truck className={`w-5 h-5 mb-2 ${fulfillment === "delivery" ? "text-dill" : "text-ink/40"}`} />
                        <p className="font-semibold text-sm text-ink">
                          Delivery <span className="text-xs font-normal text-ink/50">(OKC Metro)</span>
                        </p>
                        <p className="text-xs text-ink/55 mt-0.5">$2.00 — free over $40</p>
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50 mb-3">
                      How would you like to pay?
                    </p>
                    <div className="grid grid-cols-3 gap-2" data-testid="payment-methods">
                      {PAYMENT_METHODS.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => setPaymentMethod(m.id)}
                          className={`rounded-xl border-2 p-3 text-center transition-colors duration-300 ${
                            paymentMethod === m.id
                              ? "border-dill bg-dill/5"
                              : "border-parchborder hover:border-dill/40"
                          }`}
                          data-testid={`payment-method-${m.id}`}
                        >
                          <m.icon className={`w-5 h-5 mx-auto mb-1.5 ${paymentMethod === m.id ? "text-dill" : "text-ink/40"}`} />
                          <p className="font-semibold text-xs text-ink">{m.label}</p>
                          <p className="text-[10px] text-ink/55 mt-0.5 leading-tight">{m.desc}</p>
                        </button>
                      ))}
                    </div>

                    <AnimatePresence initial={false} mode="wait">
                      {paymentMethod === "cashapp" && (
                        <motion.div
                          key="cashapp"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                          data-testid="payment-dropdown-cashapp"
                        >
                          <a
                            href={`https://cash.app/$${cashtag.replace("$", "")}/${total.toFixed(2)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 flex items-center justify-center gap-2 bg-[#00D632] hover:bg-[#00b92b] text-white font-bold rounded-xl py-3 text-sm transition-colors"
                            data-testid="drawer-cashapp-link"
                          >
                            <Banknote className="w-4 h-4" />
                            Pay {fmt(total)} on CashApp — {cashtag}
                            <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                          </a>
                        </motion.div>
                      )}
                      {paymentMethod === "venmo" && (
                        <motion.div
                          key="venmo"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                          data-testid="payment-dropdown-venmo"
                        >
                          <a
                            href={`https://venmo.com/${venmoUser}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 flex items-center justify-center gap-2 bg-[#008CFF] hover:bg-[#0074d4] text-white font-bold rounded-xl py-3 text-sm transition-colors"
                            data-testid="drawer-venmo-link"
                          >
                            <Wallet className="w-4 h-4" />
                            Pay {fmt(total)} on Venmo — @{venmoUser}
                            <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                          </a>
                        </motion.div>
                      )}
                      {paymentMethod === "applepay" && (
                        <motion.div
                          key="applepay"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                          data-testid="payment-dropdown-applepay"
                        >
                          <div className="mt-3 rounded-xl bg-dill/5 border border-dill/20 p-4 text-center">
                            <Smartphone className="w-5 h-5 mx-auto mb-1.5 text-dill" />
                            <p className="text-xs text-ink/60 leading-relaxed">
                              After placing your order, Apple Pay {fmt(total)} to{" "}
                              <span className="font-mono font-bold text-ink">{applePay}</span> — pop your
                              batch code in the note so we can match it to your jars.
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {fulfillment === "delivery" && subtotal < FREE_DELIVERY_AT && (
                    <div data-testid="free-delivery-progress">
                      <div className="flex justify-between font-mono text-[10px] uppercase tracking-widest text-ink/50 mb-1.5">
                        <span>{fmt(FREE_DELIVERY_AT - subtotal)} away from free delivery</span>
                        <span>{Math.round(progress)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-parchdeep overflow-hidden">
                        <motion.div
                          animate={{ width: `${progress}%` }}
                          transition={{ duration: 0.5 }}
                          className="h-full rounded-full bg-gold"
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name *"
                      className="w-full bg-white border border-parchborder rounded-xl px-4 py-3 text-sm outline-none focus:border-dill transition-colors"
                      data-testid="checkout-name-input"
                    />
                    <input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Phone (for pickup/delivery updates)"
                      className="w-full bg-white border border-parchborder rounded-xl px-4 py-3 text-sm outline-none focus:border-dill transition-colors"
                      data-testid="checkout-phone-input"
                    />
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      type="email"
                      placeholder="Email (for order updates)"
                      className="w-full bg-white border border-parchborder rounded-xl px-4 py-3 text-sm outline-none focus:border-dill transition-colors"
                      data-testid="checkout-email-input"
                    />
                    {fulfillment === "delivery" && (
                      <input
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Delivery address *"
                        className="w-full bg-white border border-parchborder rounded-xl px-4 py-3 text-sm outline-none focus:border-dill transition-colors"
                        data-testid="checkout-address-input"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-parchborder px-6 py-5 bg-parchdeep/60">
                {appliedPromo ? (
                  <div className="flex items-center justify-between bg-dill/10 border border-dill/25 rounded-xl px-4 py-2.5 mb-4" data-testid="promo-applied-pill">
                    <span className="font-mono text-xs font-bold text-dill">
                      {appliedPromo.code} — {appliedPromo.type === "percent"
                        ? `${appliedPromo.value}% off`
                        : appliedPromo.type === "free_delivery"
                          ? "Free delivery"
                          : `Buy ${appliedPromo.buy_x} get ${appliedPromo.get_y} free`}
                    </span>
                    <button
                      onClick={() => setAppliedPromo(null)}
                      className="text-ink/40 hover:text-terra transition-colors"
                      aria-label="Remove promo"
                      data-testid="promo-remove-button"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2 mb-4">
                    <input
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value)}
                      placeholder="Promo code"
                      className="flex-1 bg-white border border-parchborder rounded-xl px-4 py-2.5 text-sm outline-none focus:border-dill transition-colors uppercase"
                      data-testid="promo-code-input"
                    />
                    <button
                      onClick={applyPromo}
                      disabled={promoBusy || !promoInput.trim()}
                      className="bg-dill hover:bg-terra disabled:opacity-50 text-parchment font-semibold rounded-xl px-5 py-2.5 text-sm transition-colors flex items-center gap-2"
                      data-testid="promo-apply-button"
                    >
                      {promoBusy && <Loader2 className="w-4 h-4 animate-spin" />} Apply
                    </button>
                  </div>
                )}
                <div className="space-y-1.5 text-sm mb-4">
                  <div className="flex justify-between text-ink/70">
                    <span>Subtotal</span>
                    <span data-testid="cart-subtotal">{fmt(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-ink/70">
                    <span>{fulfillment === "pickup" ? "Pickup" : "Delivery"}</span>
                    <span data-testid="cart-delivery-fee">{effectiveFee === 0 ? "FREE" : fmt(effectiveFee)}</span>
                  </div>
                  {itemDiscount > 0 && (
                    <div className="flex justify-between text-dill font-semibold">
                      <span>Promo {appliedPromo.code}</span>
                      <span data-testid="cart-promo-discount">−{fmt(itemDiscount)}</span>
                    </div>
                  )}
                  {freeDelivery && deliveryFee > 0 && (
                    <div className="flex justify-between text-dill font-semibold">
                      <span>Promo {appliedPromo.code}</span>
                      <span data-testid="cart-promo-discount">Free delivery</span>
                    </div>
                  )}
                  <div className="flex justify-between font-serif font-bold text-lg text-ink pt-1.5 border-t border-parchborder">
                    <span>Total</span>
                    <span data-testid="cart-total">{fmt(total)}</span>
                  </div>
                  <p className="text-right font-mono text-[10px] uppercase tracking-widest text-ink/40" data-testid="tax-inclusive-note">
                    All prices include sales tax
                  </p>
                </div>
                <button
                  onClick={placeOrder}
                  disabled={placing}
                  className="w-full bg-terra hover:bg-terra-hover disabled:opacity-60 text-parchment font-bold rounded-full py-4 text-sm uppercase tracking-widest transition-colors duration-300 flex items-center justify-center gap-2"
                  data-testid="cart-checkout-button"
                >
                  {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {placing ? "Placing your order…" : `Place Order — ${fmt(total)}`}
                </button>
                <p className="text-center font-mono text-[10px] text-ink/40 mt-3 uppercase tracking-widest">
                  Apple Pay • CashApp • Venmo — your choice
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
